import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
// Import Components
import PartnersListWithPartnerGroup from 'components/common/PartnersListWithPartnerGroup';
// Import UI Components
import { Empty, ExtendedInput, Option } from '@geneui/components';
// Import Constants
import { l } from 'constants/common';
//Import SCSS
import 'assets/scss/partners.scss';

const PartnersOptionList = ({
    groupsListData,
    titles,
    selectedPartner,
    selectHandler,
    listWidth,
    listHeight,
    listRowHeight,
}) => {
    const { t } = useTranslation();
    const searchRef = useRef<HTMLInputElement>(null);
    const searchHandlerTimeout = useRef<Nullable<NodeJS.Timeout>>(null);
    const groupHeightsRef = useRef([]);

    const [data, setData] = useState(groupsListData);
    const [searchValue, setSearchValue] = useState('');

    const getTitle = useMemo(() => {
        return titles.map((element, index) => {
            return (
                <span key={element} className={`crm-option-header-column-${index}`}>
                    {element}
                </span>
            );
        });
    }, [titles]);

    const searchHandler = (e) => {
        setSearchValue(e.target.value);
    };

    useEffect(() => {
        if (searchHandlerTimeout.current) {
            clearTimeout(searchHandlerTimeout.current);
        }
        searchHandlerTimeout.current = setTimeout(() => {
            const filteredList = groupsListData
                .map((group) => {
                    return {
                        ...group,
                        items: group.items.filter(({ label, displayId }) => {
                            return (
                                label.toLowerCase().includes(searchValue.trim().toLowerCase()) ||
                                +displayId === +searchValue
                            );
                        }),
                    };
                })
                .filter((partnerGroup) => partnerGroup.items.length > 0);
            setData(filteredList);
        }, 300);
    }, [groupsListData, searchValue]);

    const init = () => {
        searchRef.current?.focus();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <div className="crm-option-list-content">
            <ExtendedInput
                ref={searchRef}
                value={searchValue}
                onChange={searchHandler}
                icon="bc-icon-search"
                className="partner-search-input"
            />
            <Option title={getTitle} border="bottom" className="crm-option-list-content-column-names" />
            <div className={`partners-group-wrapper ${isEmpty(data) ? 'small-empty-option-list' : ''}`}>
                <>
                    {!isEmpty(data) ? (
                        data.map((group, i) => {
                            return (
                                <PartnersListWithPartnerGroup
                                    groupHeightsRef={groupHeightsRef}
                                    groupIndex={i}
                                    data={group}
                                    key={`${group.groupId}_${i}_${group.groupName}`}
                                    selectHandler={selectHandler}
                                    withGroupLabel={groupsListData.length > 1}
                                    hasSearchValue={!!searchValue}
                                    listWidth={listWidth}
                                    listHeight={listHeight}
                                    listRowHeight={listRowHeight}
                                    selectedPartner={selectedPartner}
                                />
                            );
                        })
                    ) : (
                        <Empty title={t(l.NoDataToDisplay)} size="small" />
                    )}
                </>
            </div>
        </div>
    );
};

PartnersOptionList.propTypes = {
    groupsListData: PropTypes.array,
    titles: PropTypes.array,
    selectHandler: PropTypes.func.isRequired,
    selectedPartner: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    listWidth: PropTypes.number,
    listHeight: PropTypes.number,
    listRowHeight: PropTypes.number,
};

export default PartnersOptionList;
